/**
 * Debounced + beforeunload persistence for request draft snapshots (#1114).
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

const DEFAULT_DEBOUNCE_MS = 400;

export function useDebouncedCallback(
  callback: () => void,
  deps: readonly unknown[],
  enabled: boolean,
  debounceMs = DEFAULT_DEBOUNCE_MS,
): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = setTimeout(callback, debounceMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies explicit deps
  }, [enabled, debounceMs, callback, ...deps]);
}

export function useBeforeUnloadPersist(
  persistSync: () => void,
  enabled: boolean,
): void {
  const persistRef = useRef(persistSync);
  persistRef.current = persistSync;

  useEffect(() => {
    if (!enabled || Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const handler = () => {
      persistRef.current();
    };
    window.addEventListener('beforeunload', handler);
    window.addEventListener('pagehide', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      window.removeEventListener('pagehide', handler);
    };
  }, [enabled]);
}
